/**
 * @description Hook utilizado para manipular os vestígios
 * @author Diego Sabino Amorim de Araújo
 * @file useVestigioService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { downloadURL } from 'utils/helpers'
import { useFetch } from './useFetch'
import constants from 'utils/constants'

export const useVestigioService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarVestigiosDeCaso = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`vestigios/deCaso/${idCaso}`)
      dispatch({
        type: actionTypes.SET_VESTIGIOS_CASO,
        vestigios: response.data.vestigios,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`vestigios/deCaso/${idCaso}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigio = async (id, setVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/${id}`)
      setVestigio(response.data.vestigio)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/${id}`)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioDetalhado = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/detalharVestigio/${id}`)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioComExameSolicitado = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`vestigios/${id}/examesSolicitados`)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioTimeline = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`vestigios/${id}/timeline`)
      dispatch({
        type: actionTypes.SET_VESTIGIO_TIMELINE,
        vestigioTimeLine: response.data.vestigio,
      })
      return response.data.vestigioTimeLine
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioTimelineAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)

      const response = await fetch.get(`vestigios/${id}/timeline`)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposVestigios = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tiposVestigios/`)
      dispatch({
        type: actionTypes.TIPOS_VESTIGIOS_GERAIS,
        tiposVestigios: response.data.tiposVestigios,
      })
      return response.data.tiposVestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposVestigiosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tiposVestigios`)
      return response.data.tiposVestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposVestigiosEspecificosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tiposVestigios/tiposEspecificos/todos`)
      return response.data.tiposVestigiosEspecificos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposVestigiosGenericos = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/tiposVestigios/genericos`)
      dispatch({
        type: actionTypes.TIPOS_VESTIGIOS,
        tiposVestigiosGenericos: response.data.tiposVestigios,
      })
      return response.data.tiposVestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposVestigiosEspecificos = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tiposVestigios/tiposEspecificos/todos`)
      dispatch({
        type: actionTypes.TIPOS_VESTIGIOS_ESPECIFICOS,
        tiposVestigiosEspecificos: response.data.tiposVestigiosEspecificos,
      })
      return response.data.tiposVestigiosEspecificos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosLikeString = async (string, setProcessando, id) => {
    if (setProcessando) setProcessando(true)
    const response = await fetch.get(`/vestigios/like/${string}`)

    if (setProcessando) setProcessando(false)
    return { vestigios: response.data.vestigios, id }
  }

  const filtrarVestigios = async (dados, setVestigios, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const filtros = {
      setores: dados.setores ? dados.setores.map((setor) => setor.id) : null,
      setoresOrigem: dados.setoresOrigem ? dados.setoresOrigem.map((setor) => setor.id) : null,
      tipos: dados.tipos ? dados.tipos.map((tipo) => tipo.id) : null,
      tiposEspecificos: dados.tiposEspecificos ? dados.tiposEspecificos.map((tipo) => tipo.id) : null,
      usuariosCadastro: dados.usuariosCadastro ? dados.usuariosCadastro.map((usuario) => usuario.id) : null,
      dataInicio: dados.dataChecked && dados.dataInicio ? dados.dataInicio : null,
      dataFinal: dados.dataChecked && dados.dataFinal ? dados.dataFinal : null,
      dataEntregaInicio: dados.dataEntregaChecked && dados.dataEntregaInicio ? dados.dataEntregaInicio : null,
      dataEntregaFinal: dados.dataEntregaChecked && dados.dataEntregaFinal ? dados.dataEntregaFinal : null,
      statusCadeiaCustodia: dados.statusCadeiaCustodia && dados.statusCadeiaCustodia.length ? dados.statusCadeiaCustodia : null,
      descricao: dados.descricao,
      lacreAtual: dados.lacreAtual,
      localizacao: dados.localizacao,
      lote: dados.lote,
    }

    const response = await fetch.postOpenWithoutFeedback(`/vestigios/filtrados`, filtros)
    setVestigios(response.data.vestigios)
    if (setProcessando) setProcessando(false)
  }

  /**
  @deprecated since version 2.3.19
  */
  const cadastrarVestigio = async (dados, idCaso, setProcessando) => {
    if (setProcessando) setProcessando(true)

    const novoVestigio = {
      id_caso: idCaso,
      id_setor_recebimento: dados.setRec ? dados.setRec.id : null,
      id_usuario_solicitante: dados.usuSol ? dados.usuSol.id : null,
      usuarioSolicitanteInput: dados.usuarioSolicitanteInput,
      lacre: dados.lacre,
      lacreAcondicionamento: dados.lacre,
      numeroVestigioAssociado: dados.numeroVestigioAssociado,
      tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
      tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
      descricao: dados.descricao,
      tipoEmbalagem: dados.tipoEmbalagem,
      outroTipoEmbalagem: dados.outroTipoEmbalagem,
      corEmbalagem: dados.corEmbalagem,
      outraCorEmbalagem: dados.outraCorEmbalagem,
      tipoFecho: dados.tipoFecho,
      outroTipoFecho: dados.outroTipoFecho,
      logomarca: dados.logomarca === 'SIM' || false,
      tipoLogomarca: dados.tipoLogomarca,
      tamanhoObjeto: dados.tamanhoObjeto,
      id_endereco_origem: dados.endOrig ? dados.endOrig.id : null,
      id_usuario_fixacao: dados.usuFix ? dados.usuFix.id : null,
      dataFixacao: dados.dataFixacao,
      fixacaoInput: dados.fixacaoInput,
      id_usuario_coleta: dados.usuCol ? dados.usuCol.id : null,
      dataColeta: dados.dataColeta,
      coletaInput: dados.coletaInput,
      id_usuario_acondicionamento: dados.usuAcond ? dados.usuAcond.id : null,
      dataAcondicionamento: dados.dataAcondicionamento,
      acondicionamentoInput: dados.acondicionamentoInput,
      id_usuario_transporte: dados.usuTrans ? dados.usuTrans.id : null,
      dataTransporte: dados.dataTransporte,
      transporteInput: dados.transporteInput,
      arquivos: dados.arquivos,
    }

    const response = await fetch.postOpen('/vestigios/', novoVestigio)

    return response.data.novoVestigio
  }

  const cadastrarMultiplosVestigios = async (dados, idCaso) => {
    const novosVestigios = {
      id_caso: idCaso,
      id_setor_recebimento: dados.setRec ? dados.setRec.id : null,
      id_usuario_solicitante: dados.usuSol ? dados.usuSol.id : null,
      usuarioSolicitanteInput: dados.usuarioSolicitanteInput,
      id_endereco_origem: dados.endOrig ? dados.endOrig.id : null,
      id_usuario_fixacao: dados.usuFix ? dados.usuFix.id : null,
      dataFixacao: dados.dataFixacao,
      fixacaoInput: dados.fixacaoInput,
      id_usuario_coleta: dados.usuCol ? dados.usuCol.id : null,
      dataColeta: dados.dataColeta,
      coletaInput: dados.coletaInput,
      id_usuario_acondicionamento: dados.usuAcond ? dados.usuAcond.id : null,
      dataAcondicionamento: dados.dataAcondicionamento,
      acondicionamentoInput: dados.acondicionamentoInput,
      id_usuario_transporte: dados.usuTrans ? dados.usuTrans.id : null,
      dataTransporte: dados.dataTransporte,
      transporteInput: dados.transporteInput,
      arquivos: dados.arquivos,
      vestigios: dados.caracteristicasVestigio.map((caracteristica) => ({
        quantidade: caracteristica.quantidade,
        lacre: caracteristica.lacre,
        lacreAcondicionamento: caracteristica.lacre,
        numeroVestigioAssociado: caracteristica.numeroVestigioAssociado,
        tipoEmbalagem: caracteristica.tipoEmbalagem,
        outroTipoEmbalagem: caracteristica.outroTipoEmbalagem,
        tiposGens: caracteristica.tiposGens ? caracteristica.tiposGens.map((t) => t.id) : null,
        tiposEsps: caracteristica.tiposEsps ? caracteristica.tiposEsps.map((t) => t.id) : null,
        descricao: caracteristica.descricao,
        tamanhoObjeto: caracteristica.tamanhoObjeto,
      })),
    }

    return await fetch.postOpen('/vestigios/multiplos', novosVestigios)
  }

  const editarVestigio = async (id, dados, setProcessando, lacreAtualHabilitado) => {
    if (setProcessando) setProcessando(true)
    const dadosVestigio = {
      id_setor_recebimento: dados.setRec ? dados.setRec.id : null,
      quantidade: dados.quantidade,
      // id_usuario_solicitante: dados.usuSol ? dados.usuSol.id : null,
      // usuarioSolicitanteInput: dados.usuarioSolicitanteInput,
      tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
      tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
      lacre: lacreAtualHabilitado ? dados.lacre : dados.lacreAcondicionamento,
      lacreAcondicionamento: dados.lacreAcondicionamento,
      numeroVestigioAssociado: dados.numeroVestigioAssociado,
      descricao: dados.descricao,
      tipoEmbalagem: dados.tipoEmbalagem,
      outroTipoEmbalagem: dados.outroTipoEmbalagem,
      corEmbalagem: dados.corEmbalagem,
      outraCorEmbalagem: dados.outraCorEmbalagem,
      tipoFecho: dados.tipoFecho,
      outroTipoFecho: dados.outroTipoFecho,
      logomarca: dados.logomarca === 'SIM' || false,
      tipoLogomarca: dados.tipoLogomarca,
      tamanhoObjeto: dados.tamanhoObjeto,
      id_endereco_origem: dados.endOrig ? dados.endOrig.id : null,
      id_usuario_fixacao: dados.usuFix ? dados.usuFix.id : null,
      dataFixacao: dados.dataFixacao,
      fixacaoInput: dados.fixacaoInput,
      id_usuario_coleta: dados.usuCol ? dados.usuCol.id : null,
      dataColeta: dados.dataColeta,
      coletaInput: dados.coletaInput,
      id_usuario_acondicionamento: dados.usuAcond ? dados.usuAcond.id : null,
      dataAcondicionamento: dados.dataAcondicionamento,
      acondicionamentoInput: dados.acondicionamentoInput,
      id_usuario_transporte: dados.usuTrans ? dados.usuTrans.id : null,
      dataTransporte: dados.dataTransporte,
      transporteInput: dados.transporteInput,
      arquivos: dados.arquivos,
      id_setor_atual: dados.id_setor_atual,
      contraprova: dados.contraprova,
    }

    await fetch.putOpen(`/vestigios/${id}`, dadosVestigio)
    if (setProcessando) setProcessando(false)
  }

  const editarVestigioDeExtracao = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosEdicao = {
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        tamanhoObjeto: dados.tamanhoObjeto,
        tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
        tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
        descricao: dados.descricao,
        contraprova: dados.contraprova,
        id_setor_atual: dados.id_setor_atual,
      }
      await fetch.putOpen(`/vestigios/deExtracao/${id}`, dadosEdicao)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const deletarVestigio = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/vestigios/${id}`)
    if (setProcessando) setProcessando(false)
  }

  const aceitarVestigio = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.put(`/vestigio/aceitar/${id}`, {
      avatar: 'AssignmentLateIcon',
    })
    if (setProcessando) setProcessando(false)
  }

  const aceitarEncaminhamentoVestigioTerceiros = async (id, usuarioTerceiro, setProcessando) => {
    if (setProcessando) setProcessando(true)
    try {
      await fetch.put(`/vestigio/aceitarEncaminhadoTerceiro/${id}`, {
        ...usuarioTerceiro,
        avatar: 'AssignmentLateIcon',
      })
    } catch (error) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const negarVestigio = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.put(`/vestigio/negar/${id}`, {
      avatar: 'AssignmentLateIcon',
    })
    if (setProcessando) setProcessando(false)
  }

  const encaminharVestigio = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const encaminhar = {
        lacre: values.lacre,
        tipoEmbalagem: values.tipoEmbalagem,
        outroTipoEmbalagem: values.outroTipoEmbalagem,
        corEmbalagem: values.corEmbalagem,
        outraCorEmbalagem: values.outraCorEmbalagem,
        tipoFecho: values.tipoFecho,
        outroTipoFecho: values.outroTipoFecho,
        logomarca: values.logomarca === 'SIM' || false,
        tipoLogomarca: values.tipoLogomarca,
        localizacao: values.localizacao,
        setDest: values.setDest ? values.setDest.id : null,
        statusCCSituacaoVestigio: values.setDest?.id === 184 ? values.situacaoVest : null,
        setorDestinoVestigio: values.setDest?.id === 184 ? values.setEntregaVest?.id : null,
        observacoes: values.observacoesVestigio,
      }

      await fetch.putOpen(`/vestigio/encaminhar/${id}`, encaminhar)
      
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cancelarEncaminhamentoVestigio = async (id, observacoes, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const data = {
      observacoes,
    }
    await fetch.putOpen(`/vestigio/cancelar/${id}`, { data })
    if (setProcessando) setProcessando(false)
  }

  const retirarVestigio = async (vestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/vestigio/retirar/${vestigio}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const tornarContraprova = async (vestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/vestigios/tornarContraprova/${vestigio}`)
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const guardarVestigio = async (id, values, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const dados = {
      lacre: values.lacre,
      tipoEmbalagem: values.tipoEmbalagem,
      outroTipoEmbalagem: values.outroTipoEmbalagem,
      corEmbalagem: values.corEmbalagem,
      outraCorEmbalagem: values.outraCorEmbalagem,
      tipoFecho: values.tipoFecho,
      outroTipoFecho: values.outroTipoFecho,
      logomarca: values.logomarca === 'SIM' || false,
      tipoLogomarca: values.tipoLogomarca,
      localizacao: values.localizacao,
    }
    await fetch.postOpen(`/vestigio/guardar/${id}`, dados)
    if (setProcessando) setProcessando(false)
  }

  const desmembrarVestigio = async (id, values, setProcessando) => {
    setProcessando(true)
    const dados = {
      vestigios: Object.keys(values).map((key) => ({
        descricao: values[key].descricao,
        lacre: values[key].lacre,
        exames: values[key].exames ? values[key].exames.map((exame) => exame.id) : [],
        id_tipo_vestigio: values[key].tipoVest && values[key].tipoVest.identificador !== 'VESTIGIO_GENERICO' ? values[key].tipoVest.id : null,
      })),
    }

    await fetch.post(`/vestigios/desmembrar/${id}`, dados)
    setProcessando(false)
  }

  const classificarVestigio = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_tipo_vestigio: values.tipoVestigio ? values.tipoVestigio.id : null,
      }
      await fetch.post(`/vestigios/classificar/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const descartarVestigio = async (id, values, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.putOpen(`/vestigio/descartar/${id}`, values)
    if (setProcessando) setProcessando(false)
  }

  const listarVestigiosBalisticosValidosClassificadosDeExame = async (id, setVestigios, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/balisticos/classificados/deExame/${id}`)
      setVestigios(response.data.vestigios)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioBalisticoDetalhado = async (id, setVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigioBalistico/detalhado/${id}`)
      setVestigio(response.data.vestigio)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const extrairVestigioDeCadaver = async (idCadaver, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosExtracao = {
        id_cadaver_origem: idCadaver,
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        tamanhoObjeto: dados.tamanhoObjeto,
        tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
        tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
        descricao: dados.descricao,
        contraprova: dados.contraprova,
        id_setor_atual: dados.setorAtual ? dados.setorAtual.id : null,
      }
      const response = await fetch.postOpen(`/vestigios/extrair/deCadaver`, dadosExtracao)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const extrairVestigioDeVestigio = async (idVestigio, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosExtracao = {
        id_vestigio_origem: idVestigio,
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        tamanhoObjeto: dados.tamanhoObjeto,
        tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
        tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
        descricao: dados.descricao,
        contraprova: dados.contraprova,
        id_setor_atual: dados.setorAtual ? dados.setorAtual.id : null,
        caso: dados.caso ? dados.caso.id : null,
      }
      const response = await fetch.postOpen(`/vestigios/extrair/deVestigio`, dadosExtracao)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  // RF 06 - extrair vestígio do veículo

  const extrairVestigioDeVeiculo = async (idVeiculo, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosExtracao = {
        id_veiculo_origem: idVeiculo,
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        tamanhoObjeto: dados.tamanhoObjeto,
        tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
        tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
        descricao: dados.descricao,
        contraprova: dados.contraprova,
        id_setor_atual: dados.setorAtual ? dados.setorAtual.id : null,
        caso: dados.caso ? dados.caso.id : null,
      }
      const response = await fetch.postOpen(`/vestigios/extrair/deVeiculo`, dadosExtracao)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const extrairVestigioDePericiando = async (idPericiando, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosExtracao = {
        ...dados,
        id_periciando_origem: idPericiando,
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        tamanhoObjeto: dados.tamanhoObjeto,
        tiposGens: dados.tiposGens ? dados.tiposGens.map((t) => t.id) : null,
        tiposEsps: dados.tiposEsps ? dados.tiposEsps.map((t) => t.id) : null,
        descricao: dados.descricao,
        contraprova: dados.contraprova,
        id_setor_atual: dados.setorAtual ? dados.setorAtual.id : null,
        caso: dados.caso ? dados.caso.id : null,
      }
      const response = await fetch.postOpen(`/vestigios/extrair/dePericiando`, dadosExtracao)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosExtraidosDeVestigioAsync = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/extraidos/deVestigio/${idVestigio}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosExtraidosDeCadaverAsync = async (idCadaver, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/extraidos/deCadaver/${idCadaver}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  // RF 06 - extrair vestígio do veículo

  const listarVestigiosExtraidosDeVeiculoAsync = async (idVeiculo, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/extraidos/deVeiculo/${idVeiculo}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosExtraidosDePericiandoAsync = async (idPericiando, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/extraidos/dePericiando/${idPericiando}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const verificarVestigioEmCustodiaDeUsuario = (vestigio, usuario) => {
    if (!vestigio || !usuario) return false
    return vestigio.id_usuario_atual && vestigio.id_usuario_atual === usuario.id && vestigio.statusCadeiaCustodia === 'COM_USUARIO'
  }

  const solicitarPassagemCustodia = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosPassagemCustodia = {
        lacre: values.lacre,
        tipoEmbalagem: values.tipoEmbalagem,
        outroTipoEmbalagem: values.outroTipoEmbalagem,
        corEmbalagem: values.corEmbalagem,
        outraCorEmbalagem: values.outraCorEmbalagem,
        tipoFecho: values.tipoFecho,
        outroTipoFecho: values.outroTipoFecho,
        logomarca: values.logomarca === 'SIM' || false,
        tipoLogomarca: values.tipoLogomarca,
        idUsuarioDestino: values.usuarioDestino ? values.usuarioDestino.id : null,
        observacao: values.observacao,
      }
      await fetch.putOpen(`/vestigio/passagemCustodia/solicitar/${id}`, dadosPassagemCustodia)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const solicitarMultiplasPassagemCustodia = async (idsVestigios, dados) => {
    try {
      const dadosPassagemCustodia = {
        idsVestigios,
        id_usuario_destino: dados.usuarioDestino ? dados.usuarioDestino.id : null,
        observacao: dados.observacao,
        ...(dados.lacre && { lacre: dados.lacre }),
        ...(dados.tipoEmbalagem && { tipoEmbalagem: dados.tipoEmbalagem, outroTipoEmbalagem: dados.outroTipoEmbalagem }),
      }
      await fetch.putOpen(`/vestigio/passagemCustodia/multiplas/solicitar`, dadosPassagemCustodia)
    } catch (err) {
      throw err
    }
  }

  const aceitarPassagemCustodia = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/vestigio/passagemCustodia/aceitar/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const aceitarMultiplasPassagemCustodia = async (idsVestigios, dados) => {
    try {
      await fetch.putOpen(`/vestigio/passagemCustodia/multiplos/aceitar`, { idsVestigios, usuarioDestino: dados?.usuarioDestino ? dados.usuarioDestino : null })
    } catch (err) {
      throw err
    }
  }

  const aceitarPassagemCustodiaTerceiros = async (id, usuarioTerceiro, setProcessando) => {
    if (setProcessando) setProcessando(true)
    try {
      await fetch.putOpen(`/vestigio/passagemCustodia/aceitarTerceiro/${id}`, usuarioTerceiro)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cancelarPassagemCustodia = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/vestigio/passagemCustodia/cancelar/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const negarPassagemCustodia = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/vestigio/passagemCustodia/negar/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const negarMultiplasPassagemCustodia = async (idsVestigios) => {
    try {
      await fetch.putOpen(`/vestigio/passagemCustodia/multiplas/negar/`, { idsVestigios })
    } catch (err) {
      throw err
    }
  }

  const listarUltimaPassagemCustodia = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigio/passagemCustodia/listarultima/${id}`)
      return response.data.passagemCustodia
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUltimoEncaminhamento = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigio/encaminhamento/listarultimo/${id}`)
      return response.data.encaminhamento
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUltimaOperacaoGuardarAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigio/guardamento/listarultima/${id}`)
      return response.data.guardamento
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const entregarVestigio = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosEntrega = {
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        logomarca: dados.logomarca === 'SIM' || false,
        tipoLogomarca: dados.tipoLogomarca,
        idUsuarioRecebedor: dados.usuarioRecebedor ? dados.usuarioRecebedor.id : null,
        usuarioRecebedorInput: dados.usuarioRecebedorInput,
        idSetorDestino: dados.setorDestino ? dados.setorDestino.id : null,
        arquivos: dados.arquivos,
      }
      await fetch.putOpen(`/vestigio/entregar/${id}`, dadosEntrega)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const retornarVestigio = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosRetorno = {
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        logomarca: dados.logomarca === 'SIM' || false,
        tipoLogomarca: dados.tipoLogomarca,

        idUsuarioOrigem: dados.usuOrig ? dados.usuOrig.id : null,
        usuarioOrigemInput: dados.usuarioOrigemInput,
        idSetorOrigem: dados.setOrig ? dados.setOrig.id : null,
        idSetorDestino: dados.setDest ? dados.setDest.id : null,

        arquivos: dados.arquivos,
      }
      return await fetch.putOpen(`/vestigio/retornar/${id}`, dadosRetorno)
    } catch (err) {
      throw err
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const entregarMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosEntrega = {
        idsVestigios,
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        idUsuarioRecebedor: dados.usuarioDestino?.id,
        idSetorDestino: dados.setDest?.id,
        entregaConfirmada: dados.entregaConfirmada,
      }
      await fetch.putOpen('/vestigio/entregarmultiplos/', dadosEntrega)
    } catch (err) {
      throw err
    }
  }

  const retornarMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosRetorno = {
        idsVestigios,
        ...(dados.lacre && { lacre: dados.lacre }),
        ...(dados.tipoEmbalagem && { tipoEmbalagem: dados.tipoEmbalagem }),
        idUsuarioOrigem: dados.usuOrig ? dados.usuOrig.id : null,
        usuarioOrigemInput: dados.usuarioOrigemInput,
        idSetorOrigem: dados.setOrig ? dados.setOrig.id : null,
        idSetorDestino: dados.setDest ? dados.setDest.id : null,
        arquivos: dados.arquivos,
      }
      return await fetch.putOpen(`/vestigio/retornarmultiplos`, dadosRetorno)
    } catch (err) {
      throw err
    }
  }

  const guardarMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosGuardar = {
        idsVestigios,
        localizacao: dados.localizacao,
        ...(dados.lacre && { lacre: dados.lacre }),
        ...(dados.tipoEmbalagem ? { tipoEmbalagem: dados.tipoEmbalagem, outroTipoEmbalagem: dados.outroTipoEmbalagem } : {}),
      }
      return await fetch.putOpen(`/vestigio/guardarmultiplos`, dadosGuardar)
    } catch (err) {
      throw err
    }
  }

  const retirarMultiplosVestigios = async (idsVestigios) => {
    try {
      return await fetch.putOpen(`/vestigio/retirarmultiplos`, { idsVestigios })
    } catch (err) {
      throw err
    }
  }

  const descartarMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosDescartar = {
        idsVestigios,
        id_usuario_autorizacao_descarte: dados.usuAutorizacao.id,
        numeroAutorizacaoDescarte: dados.numDocumento,
        dataAutorizacaoDescarte: dados.data,
        observacoesDescarte: dados.observacao
      }
      return await fetch.putOpen(`/vestigio/descartarmultiplos`, dadosDescartar)
    } catch (err) {
      throw err
    }
  }

  const alterarSituacaoMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosAlteracao = {
        idsVestigios,
        statusCCSituacaoVestigio: dados.situacaoVest,
        observacoesSituacaoVestigio: dados.numDocumento,
        setorDestinoVestigio: dados.setEntregaVest ? dados.setEntregaVest.id : null,
      }
      return await fetch.putOpen(`/vestigio/alterarsituacaomultiplos`, dadosAlteracao)
    } catch (err) {
      throw err
    }
  }

  const encaminharMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosEncaminhamento = {
        idsVestigios,
        setDest: dados.setDest ? dados.setDest.id : null,
        statusCCSituacaoVestigio: dados.situacaoVest ? dados.situacaoVest : null,
        setorDestinoVestigio: dados.setEntregaVest ? dados.setEntregaVest : null,
        observacao: dados.observacao,
        ...(dados.lacre && { lacre: dados.lacre }),
        ...(dados.tipoEmbalagem && { tipoEmbalagem: dados.tipoEmbalagem, outroTipoEmbalagem: dados.outroTipoEmbalagem }),
      }
      return await fetch.putOpen(`/vestigio/encaminharmultiplos`, dadosEncaminhamento)
    } catch (err) {
      throw err
    }
  }

  const aceitarMultiplosEncaminhamentos = async (idsVestigios, dados) => {
    try {
      return await fetch.putOpen(`/vestigio/encaminhamento/aceitar/multiplos`, { idsVestigios, usuarioDestino: dados?.usuarioDestino ? dados.usuarioDestino : null })
    } catch (err) {
      throw err
    }
  }

  const negarMultiplosEncaminhamentos = async (idsVestigios) => {
    try {
      return await fetch.putOpen(`/vestigio/encaminhamento/negar/multiplos`, { idsVestigios })
    } catch (err) {
      throw err
    }
  }
  const retornarVestigioAdministrador = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosRetorno = {
        lacre: dados.lacre,
        tipoEmbalagem: dados.tipoEmbalagem,
        outroTipoEmbalagem: dados.outroTipoEmbalagem,
        corEmbalagem: dados.corEmbalagem,
        outraCorEmbalagem: dados.outraCorEmbalagem,
        tipoFecho: dados.tipoFecho,
        outroTipoFecho: dados.outroTipoFecho,
        logomarca: dados.logomarca === 'SIM' || false,
        tipoLogomarca: dados.tipoLogomarca,

        idUsuarioOrigem: dados.usuOrig ? dados.usuOrig.id : null,
        usuarioOrigemInput: dados.usuarioOrigemInput,
        idUsuarioDestino: dados.usuDest ? dados.usuDest.id : null,
        idSetorOrigem: dados.setOrig ? dados.setOrig.id : null,
        idSetorDestino: dados.setDest ? dados.setDest.id : null,
        arquivos: dados.arquivos,
      }
      return await fetch.putOpen(`/vestigio/retornarAdm/${id}`, dadosRetorno)
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarEntregasVestigio = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigio/entregas/${id}`)
      return response.data.entregas
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const adicionarInformacao = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/vestigio/adicionarinformacao/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const consumirVestigio = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosConsumir = {
        observacao: dados.observacao,
        id_exame_realizado: dados.exReal ? dados.exReal.id : null,
      }
      await fetch.postOpen(`/vestigio/consumirvestigio/${id}`, dadosConsumir)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const consumirMultiplosVestigios = async (idsVestigios, dados) => {
    try {
      const dadosConsumir = {
        idsVestigios,
        observacao: dados.observacao,
        id_exame_realizado: dados.exameRealizado ? dados.exameRealizado.id : null,
      }

      await fetch.putOpen(`/vestigio/consumir/multiplos/`, dadosConsumir)
    } catch (err) {
      throw err
    }
  }

  const listarInformacoesAdicionadas = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigio/informacoes/${id}`)
      return response.data.informacoes
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosBalisticosDeExameSolicitadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigioBalistico/deExameSolicitado/${id}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigioPorTipoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/${id}/porTipo`)
      return response.data.vestigio
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarVestigioBalistico = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/vestigioBalistico/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const identificarVestigioBalistico = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/vestigioBalistico/${id}/identificar`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosDeExameSolicitadoExtraidosDeCadaverAsync = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/deExameSolicitado/extraidosCadaver/examinador/${idExameSolicitado}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosEncaminhadosParaSetoresDoUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/encaminhados/setoresdousuario`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosDeExameSolicitadoAsync = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/deExameSolicitado/paraExame/${idExameSolicitado}`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosDoUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/vestigios/doUsuario`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosEncaminhadosParaUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/vestigios/encaminhadosParaUsuario`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigiosDoSetorAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/vestigios/doSetor`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarVestigios = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/`)
      return response.data.vestigios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const importarVestigiosNoCaso = async (idCaso, vestigiosImportar, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpenWithoutFeedback(`/vestigios/importarVestigiosNoCaso/${idCaso}`, vestigiosImportar)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const gerarTermoNaoConformidade = async (idVestigio, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/vestigios/gerarTermoNaoConformidade/${idVestigio}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTermoNaoConformidade = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/listarTermoNaoConformidade/${idVestigio}`)
      return response.data.termoNaoConformidade
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const assinarTermoNaoConformidade = async (idTermo, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/vestigios/assinarTermoNaoConformidade/${idTermo}`)
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const openPDFInNewTab = (url, nome) => {
    window.open(url, 'nome_especifico')
  }

  const downloadPDFTermoNaoConformidade = async (idTermo) => {
    try {
      const response = await fetch.getWithOptions(`/vestigios/downloadTermoNaoConformidade/pdf/${idTermo}`, {
        responseType: 'blob',
      })

      const fileURL = URL.createObjectURL(response.data)

      openPDFInNewTab(fileURL, 'Termo de Não Conformidade')
    } catch (err) {
      return
    }
  }

  const listarVestigiosComCadeiaCustodia = async (ids) => {
    try {
      const response = await fetch.getWithOptions('/vestigios/cadeiacustodia/listarVestigioPorIds', { params: { ids } })
      return response.data.vestigios
    } catch (err) {
      console.log(err)
    }
  }

  const listarCasosDeVestigio = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/vestigios/listarCasosDeVestigio/${idVestigio}`)
      return response.data.casos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const criarLote = async (idVestigio) => {
    try {
      const response = await fetch.postOpen(`/vestigios/lote/cadastrar/${idVestigio}`)
      return response.data.lote
    } catch (err) {
      throw err
    }
  }

  const cadastrarEAdicionarEmLote = async (idsVestigios) => {
    try {
      const response = await fetch.postOpen(`/vestigios/lote/cadastrarEAdicionarEmLote`, { idsVestigios })
      return response.data.lote
    } catch (err) {
      throw err
    }
  }

  const adicionarVestigioEmLote = async (idVestigio, idLote) => {
    try {
      await fetch.putOpen(`/vestigios/lote/adicionar`, { idVestigio, idLote })
    } catch (err) {
      throw err
    }
  }

  const removerVestigioDeLote = async (idVestigio) => {
    try {
      await fetch.putOpen(`/vestigios/lote/remover/${idVestigio}`)
    } catch (err) {
      throw err
    }
  }

  const listarLote = async (id) => {
    try {
      const response = await fetch.get(`/vestigios/lote/listar/${id}`)
      return response.data.lote
    } catch (err) {
      throw err
    }
  }

  const listarVestigiosPorIdentificadorLikeString = async (string) => {
    try {
      const response = await fetch.get(`/vestigios/listar/poridentificador/likestring/${string}`)

      return response.data.vestigios
    } catch (err) {
      throw err
    }
  }

  const listarVestigiosNaCentralDeCustodiaPorStatus = async (status, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      switch (status) {
        case constants.STATUS_CENTRAL_CUSTODIA.PARA_ARMAZENAMENTO:
          const vestsParaArmazenamento = await fetch.get(`/vestigios/centralcustodia/paraArmazenamento`)
          return vestsParaArmazenamento.data.vestigios

        case constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA:
          const vestsParaEntrega = await fetch.get(`/vestigios/centralcustodia/paraEntrega`)
          return vestsParaEntrega.data.vestigios
          
        case constants.STATUS_CENTRAL_CUSTODIA.PARA_DESCARTE:
          const vestsParaDescarte = await fetch.get(`/vestigios/centralcustodia/paraDescarte`)
          return vestsParaDescarte.data.vestigios

        case 'ENCAMINHADOS':
          const vestsEncaminhados = await fetch.get(`/vestigios/centralcustodia/encaminhados`)
          return vestsEncaminhados.data.vestigios
      
        case 'TODOS':
          const vestsNoSetor = await fetch.get(`/vestigios/centralcustodia/todos`)
          return vestsNoSetor.data.vestigios

        default:
          return null
      }
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const alterarStatusCCdeVestigio = async ({ idVestigio, novoStatus, novoLocalGuardado, setorEntrega }, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      return await fetch.put(`/vestigios/centralcustodia/alterarStatus`, { idVestigio, novoStatus, novoLocalGuardado, setorEntrega })
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarVestigiosDeCaso,
    listarVestigiosDeCasoAsync,
    listarVestigio,
    listarVestigioAsync,
    listarVestigioDetalhado,
    listarTiposVestigiosGenericos,
    listarVestigioComExameSolicitado,
    cadastrarVestigio,
    editarVestigio,
    listarVestigioTimeline,
    listarVestigioTimelineAsync,
    deletarVestigio,
    aceitarVestigio,
    negarVestigio,
    encaminharVestigio,
    cancelarEncaminhamentoVestigio,
    retirarVestigio,
    tornarContraprova,
    guardarVestigio,
    desmembrarVestigio,
    listarTiposVestigiosEspecificos,
    classificarVestigio,
    descartarVestigio,
    filtrarVestigios,
    listarVestigiosLikeString,
    listarTiposVestigios,
    listarTiposVestigiosAsync,
    listarTiposVestigiosEspecificosAsync,
    listarVestigiosBalisticosValidosClassificadosDeExame,
    listarVestigioBalisticoDetalhado,
    extrairVestigioDeCadaver,
    extrairVestigioDeVestigio,
    listarVestigiosExtraidosDeVestigioAsync,
    listarVestigiosExtraidosDeCadaverAsync,
    verificarVestigioEmCustodiaDeUsuario,
    solicitarPassagemCustodia,
    aceitarPassagemCustodia,
    cancelarPassagemCustodia,
    negarPassagemCustodia,
    listarUltimaPassagemCustodia,
    listarUltimoEncaminhamento,
    entregarVestigio,
    listarEntregasVestigio,
    adicionarInformacao,
    listarInformacoesAdicionadas,
    extrairVestigioDeVeiculo,
    listarVestigiosExtraidosDeVeiculoAsync,
    listarVestigiosBalisticosDeExameSolicitadoAsync,
    listarVestigioPorTipoAsync,
    editarVestigioBalistico,
    identificarVestigioBalistico,
    listarVestigiosDeExameSolicitadoExtraidosDeCadaverAsync,
    extrairVestigioDePericiando,
    listarVestigiosExtraidosDePericiandoAsync,
    listarUltimaOperacaoGuardarAsync,
    consumirVestigio,
    listarVestigiosEncaminhadosParaSetoresDoUsuarioAsync,
    listarVestigiosDeExameSolicitadoAsync,
    editarVestigioDeExtracao,
    listarVestigiosDoUsuarioAsync,
    listarVestigiosEncaminhadosParaUsuarioAsync,
    listarVestigiosDoSetorAsync,
    listarVestigios,
    importarVestigiosNoCaso,
    retornarVestigio,
    retornarVestigioAdministrador,
    cadastrarMultiplosVestigios,
    gerarTermoNaoConformidade,
    listarTermoNaoConformidade,
    assinarTermoNaoConformidade,
    downloadPDFTermoNaoConformidade,
    retornarMultiplosVestigios,
    retirarMultiplosVestigios,
    descartarMultiplosVestigios,
    alterarSituacaoMultiplosVestigios,
    entregarMultiplosVestigios,
    guardarMultiplosVestigios,
    listarVestigiosComCadeiaCustodia,
    encaminharMultiplosVestigios,
    aceitarMultiplosEncaminhamentos,
    negarMultiplosEncaminhamentos,
    solicitarMultiplasPassagemCustodia,
    aceitarMultiplasPassagemCustodia,
    negarMultiplasPassagemCustodia,
    consumirMultiplosVestigios,
    listarCasosDeVestigio,
    aceitarPassagemCustodiaTerceiros,
    aceitarEncaminhamentoVestigioTerceiros,
    criarLote,
    adicionarVestigioEmLote,
    removerVestigioDeLote,
    listarLote,
    listarVestigiosPorIdentificadorLikeString,
    cadastrarEAdicionarEmLote,
    listarVestigiosNaCentralDeCustodiaPorStatus,
    alterarStatusCCdeVestigio,
  }
}
